import React, { useEffect, useMemo, useState } from "react";
import { users as usersApi } from "../api/endpoints";
import { DataTableShell, EmptyState, MetricStrip, PageHeader, SectionCard } from "../components/Workspace";
import type { User } from "../types";

const USER_PAGE_SIZE = 5000;

function fullName(user: User) {
  return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
}

function formatDateTime(value?: string | null) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

async function fetchAllActiveUsers() {
  const allUsers: User[] = [];
  let offset = 0;
  let totalCount: number | null = null;

  while (totalCount == null || allUsers.length < totalCount) {
    const params = new URLSearchParams({
      limit: String(USER_PAGE_SIZE),
      offset: String(offset),
    });
    const response = await usersApi.list(params.toString());
    allUsers.push(...response.items);
    totalCount = response.count;

    if (response.items.length === 0) break;
    offset += response.items.length;
  }

  return allUsers;
}

export default function UserPasswordsPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [passwords, setPasswords] = useState({ newPassword: "", confirmPassword: "" });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; message: string } | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await fetchAllActiveUsers();
      setUsers(result);
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Unable to load user accounts." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) =>
      [fullName(user), user.email, user.role?.name || "", user.management_company_name, user.report_management_company_name]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [search, users]);

  const metricItems = [
    { label: "Active Accounts", value: users.length, detail: "Users available for password setup", tone: "brand" as const },
    { label: "Password Enabled", value: users.filter((user) => user.has_usable_password).length, detail: "Can sign in with a password", tone: "success" as const },
    { label: "No Password", value: users.filter((user) => !user.has_usable_password).length, detail: "Needs first password set", tone: "warning" as const },
    { label: "Online", value: users.filter((user) => user.is_logged_in).length, detail: "Login flag currently active", tone: "success" as const },
  ];

  const startPasswordChange = (user: User) => {
    setSelectedUser(user);
    setPasswords({ newPassword: "", confirmPassword: "" });
    setNotice(null);
  };

  const submitPasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedUser) return;
    if (passwords.newPassword.length < 8) {
      setNotice({ tone: "danger", message: "Use at least 8 characters for the new password." });
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setNotice({ tone: "danger", message: "Password confirmation does not match." });
      return;
    }
    setSaving(true);
    try {
      await usersApi.setPassword(selectedUser.id, passwords.newPassword);
      setNotice({ tone: "success", message: `Password updated for ${fullName(selectedUser)}.` });
      setSelectedUser(null);
      setPasswords({ newPassword: "", confirmPassword: "" });
      loadUsers();
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Password update failed." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Admin Settings"
        title="User Passwords"
        description="Set or update passwords for active user accounts."
        compact
        actions={
          <button type="button" onClick={loadUsers} className="button-secondary" disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        }
      />

      <MetricStrip items={metricItems} />

      {notice ? (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            notice.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-800"
          }`.trim()}
        >
          {notice.message}
        </div>
      ) : null}

      <SectionCard title="Find Account" subtitle="Active accounts are listed here, including users who need their first password.">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="input-shell w-full"
          placeholder="Search by name, email, role, HMC, or report scope"
        />
      </SectionCard>

      <DataTableShell title="User Password Access" subtitle={`${filteredUsers.length} matching accounts`}>
        {filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Report Scope</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="font-semibold text-slate-900">{fullName(user)}</div>
                      <div className="mt-1 text-sm text-slate-600">{user.email}</div>
                    </td>
                    <td>{user.role?.name || "-"}</td>
                    <td>
                      <div>{user.report_management_company_name || user.management_company_name || "-"}</div>
                      {user.report_management_company_name ? (
                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{user.report_view_mode}</div>
                      ) : null}
                    </td>
                    <td>{formatDateTime(user.last_login)}</td>
                    <td>
                      <div className="flex flex-wrap gap-2">
                        <span className={`badge-pill ${user.has_usable_password ? "tone-brand" : "tone-warning"}`}>
                          {user.has_usable_password ? "Password enabled" : "No password"}
                        </span>
                        <span className={`badge-pill ${user.is_logged_in ? "tone-success" : "tone-neutral"}`}>
                          {user.is_logged_in ? "Online" : "Offline"}
                        </span>
                        {user.is_blocked ? <span className="badge-pill tone-danger">Blocked</span> : null}
                      </div>
                    </td>
                    <td>
                      <button type="button" onClick={() => startPasswordChange(user)} className="button-secondary !px-3 !py-2 text-xs">
                        {user.has_usable_password ? "Reset Password" : "Set Password"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No accounts found" message="No active accounts match the current search." />
        )}
      </DataTableShell>

      {selectedUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-bold text-slate-950">Set Password</h2>
            <p className="mt-2 text-sm text-slate-600">
              Updating password for <span className="font-semibold text-slate-900">{fullName(selectedUser)}</span>.
            </p>
            <form onSubmit={submitPasswordChange} className="mt-5 space-y-3">
              <input
                type="password"
                value={passwords.newPassword}
                onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })}
                className="input-shell w-full"
                placeholder="New password"
                required
              />
              <input
                type="password"
                value={passwords.confirmPassword}
                onChange={(event) => setPasswords({ ...passwords, confirmPassword: event.target.value })}
                className="input-shell w-full"
                placeholder="Confirm new password"
                required
              />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setSelectedUser(null)} className="button-secondary" disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="button-primary" disabled={saving}>
                  {saving ? "Saving..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
